import Typography from "antd/lib/typography";
import { isAfter } from "date-fns";
import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { useId } from "react-aria";
import { useTranslation } from "react-i18next";

import { useActiveClassroom } from "@sol/classrooms";
import {
    getResourceUuid,
    IClassroomUser,
    IUser,
    IValidationResource,
    useClassroomListQuery,
    useValidationListQuery,
} from "@sol/sdk";
import { OrderingDirection } from "@sol/sdk/utils/api";
import { Magnifier } from "@sol/uikit/core/icons/Magnifier";
import { Card } from "@sol/uikit/data-display/Card/Card";
import { Input } from "@sol/uikit/data-entry/Input/Input";
import { Skeleton } from "@sol/uikit/feedback/Skeleton/Skeleton";

import { lastStartedClassroom } from "@/entities/classroom";
import { UsersList } from "@/entities/user";

import { LearnerDetails as LearnersSkills } from "./LearnerDetails";
import useSearch from "../../../hooks/useSearch";

type Props = {
    isPending?: boolean;
    learners: (IClassroomUser | IUser)[];
    className?: string;
};

export const LearnersExplorer = ({ isPending, learners, className, ...rest }: Props) => {
    const [t] = useTranslation();
    const [selectedLearner, setSelectedLearner] = useState<IClassroomUser | IUser>();
    const [isLearnerSelected, setIsLearnerSelected] = useState(false);
    const { activeClassroom } = useActiveClassroom();
    const focusRef = useRef<HTMLHeadingElement>(null);
    const skipFocusRef = useRef(true);
    const searchInputId = useId();

    const { results, search, setSearch } = useSearch<IClassroomUser | IUser>(learners, { keys: ["name", "surname"] });

    useEffect(() => {
        setSelectedLearner(learners.length > 0 ? learners[0] : undefined);
        setIsLearnerSelected(false);
    }, [isPending, learners]);

    useEffect(() => {
        if (selectedLearner) {
            if (!skipFocusRef.current && isLearnerSelected) {
                focusRef?.current?.focus();
            }
            skipFocusRef.current = false;
        }
    }, [selectedLearner, isLearnerSelected]);

    //If no active classroom, we fetch selected learners classrooms order by startDate
    const { data: selectedLearnerClassrooms } = useClassroomListQuery(
        !activeClassroom
            ? {
                  order: { by: "startDate", direction: OrderingDirection.DESC },
                  filters: {
                      learners: selectedLearner && selectedLearner,
                  },
              }
            : { filters: {} },
        { enabled: !!selectedLearner && !activeClassroom },
    );

    const classrooms = useMemo(
        () =>
            activeClassroom
                ? [getResourceUuid(activeClassroom)]
                : (selectedLearnerClassrooms?.["hydra:member"] ?? []).map(classroom => getResourceUuid(classroom)),
        [activeClassroom, selectedLearnerClassrooms],
    );

    const filters = useMemo(() => {
        if (!selectedLearner || classrooms.length === 0) {
            return;
        }

        return {
            learner: getResourceUuid(selectedLearner),
            classroom: classrooms,
        };
    }, [classrooms, selectedLearner]);

    // The API rejects a validations query without a classroom filter
    const { data: skillValidationsList, isInitialLoading } = useValidationListQuery(
        { filters },
        { enabled: !!filters },
    );

    const lastLearnerClassroom = lastStartedClassroom(selectedLearnerClassrooms?.["hydra:member"], new Date());

    let itemToRender = (
        <div className=" col-span-12 flex min-h-0 min-w-0 flex-1 items-center justify-center">
            <Typography.Text className="text-center text-ant-h2 font-ant-strong">
                {t("page.classrooms.home.noLearners")}
            </Typography.Text>
        </div>
    );

    const skillsValidations: IValidationResource[] = skillValidationsList ? skillValidationsList["hydra:member"] : [];

    //Here we keep only the most recent skillValidations for a same framework found in multiple classrooms (tutor)
    const filterSkillsValidations = useMemo(() => {
        const sortedValidationsByUpdatedAt: IValidationResource[] = skillsValidations.sort(
            (firstValidation, nextValidation) =>
                new Date(nextValidation.updatedAt).getTime() - new Date(firstValidation.updatedAt).getTime(),
        );
        return sortedValidationsByUpdatedAt.reduce((acc, current) => {
            const duplicates = acc.find((validation: IValidationResource) => {
                return (
                    validation.skillLevel["@id"] === current.skillLevel["@id"] &&
                    isAfter(new Date(validation.updatedAt), new Date(current.updatedAt))
                );
            });
            if (!duplicates) {
                return acc.concat([current]);
            } else {
                return acc;
            }
        }, [] as IValidationResource[]);
    }, [skillsValidations]);

    const validations = activeClassroom ? skillsValidations : filterSkillsValidations;

    if (isPending || (learners.length > 0 && !selectedLearner)) {
        itemToRender = (
            <div className="col-span-12 size-full min-w-0 py-ant-lg">
                <Skeleton.Node className="size-full!" active />
            </div>
        );
    } else if (learners?.length > 0) {
        itemToRender = (
            <>
                <div className="flex w-3/12 flex-col border-r border-ant-secondary py-ant-lg pr-ant">
                    <div className="px-ant-xxs pb-ant-sm">
                        <Input
                            prefix={<Magnifier variant="antd" />}
                            size="large"
                            title={t("component.SelfEvaluationTrainer.searchLabel")}
                            data-cy="search"
                            value={search}
                            onChange={(event: ChangeEvent<HTMLInputElement>) => setSearch(event.currentTarget.value)}
                            placeholder={t("component.SelfEvaluationTrainer.searchPlaceholder")}
                            id={searchInputId}
                        />
                    </div>
                    <div aria-live="polite" className="overflow-auto px-ant-xxs py-ant-sm">
                        <p className="sr-only">
                            {results.length > 0
                                ? t("component.UsersList.userCount", { count: results.length })
                                : t("component.UsersList.noResult")}
                        </p>
                        <UsersList
                            users={results}
                            selectedUserId={selectedLearner ? getResourceUuid(selectedLearner) : undefined}
                            setSelectedUser={setSelectedLearner}
                        />
                    </div>
                </div>
                {selectedLearner && (
                    <div className="ml-ant w-9/12 p-ant-lg pl-0" role="tabpanel">
                        <LearnersSkills
                            lastLearnerClassroom={lastLearnerClassroom}
                            learner={selectedLearner}
                            validations={validations}
                            isLoading={isInitialLoading}
                            focusRef={focusRef}
                            className="p-0!"
                        />
                    </div>
                )}
            </>
        );
    }

    return (
        <Card className={className} classNames={{ body: "flex h-[490px] overflow-hidden !pt-0 !pb-0" }} {...rest}>
            {itemToRender}
        </Card>
    );
};
